<script lang="ts" setup>
import { reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PwStrengthMeter from '@/components/misc/PwStrengthMeter.vue'
import { STATUS_CODES } from '@/constants'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useDirtyFormGuard } from '@/composables/useDirtyFormGuard'

const { t } = useI18n()

const data = reactive({
  oldPassword: '',
  newPasswordRetype: '',
  newPassword: '',
  requestedOtp: false,
  otp: '',
  pwStrength: 0,
  passwordChanged: false,
  loading: false,
  error: ''
})

// Track dirty state - form is dirty when any password field has content
const isDirty = computed(() => {
  return !!(data.oldPassword || data.newPassword || data.newPasswordRetype || data.otp)
})
useDirtyFormGuard('account-pw-change', isDirty)

const isFormValid = computed(() => {
  if (data.pwStrength <= 1) return false
  if (!data.oldPassword) return false
  if (!data.newPassword) return false
  if (!data.newPasswordRetype) return false
  if (data.newPassword !== data.newPasswordRetype) return false
  if (data.requestedOtp && !data.otp) return false
  return true
})

const passwordsMatch = computed(() => {
  if (!data.newPassword || !data.newPasswordRetype) return true
  return data.newPassword === data.newPasswordRetype
})

const updatePw = () => {
  if (!isFormValid.value) return

  data.loading = true
  data.error = ''

  useAxios<{ old_password: string; password: string; token?: string }, null>(
    'POST',
    '/account/password',
    true,
    data.otp
      ? { old_password: data.oldPassword, password: data.newPassword, token: data.otp }
      : { old_password: data.oldPassword, password: data.newPassword }
  )
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        data.requestedOtp = true
      }
      if (res.status === STATUS_CODES.ACCEPTED) {
        data.passwordChanged = true
        setTimeout(() => {
          data.passwordChanged = false
          revokeChanges()
        }, 5000)
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'AccountPwChange' })
      data.error = t('dashboard.account.security.passwordChangeFailed')
    })
    .finally(() => {
      data.loading = false
    })
}

const revokeChanges = () => {
  data.requestedOtp = false
  data.oldPassword = ''
  data.newPassword = ''
  data.newPasswordRetype = ''
  data.otp = ''
  data.pwStrength = 0
  data.loading = false
  data.error = ''
}

const handlePwStrength = (pwStrength: number) => {
  data.pwStrength = pwStrength
}
</script>

<template>
  <div class="password-section">
    <!-- Section Header -->
    <div class="card-header">
      <div class="card-icon">
        <v-icon icon="mdi-lock-reset" size="24" />
      </div>
      <div class="card-info">
        <h3 class="card-title">{{ $t('dashboard.account.security.passwordChange') }}</h3>
        <p class="card-description">{{ $t('dashboard.account.security.passwordChangeDescription') }}</p>
      </div>
    </div>

    <!-- Success Message -->
    <Transition name="slide-fade">
      <div v-if="data.passwordChanged" class="alert alert-success">
        <v-icon icon="mdi-check-circle" size="20" />
        <span>{{ $t('dashboard.account.security.passwordChanged') }}</span>
      </div>
    </Transition>

    <!-- Error Message -->
    <Transition name="slide-fade">
      <div v-if="data.error" class="alert alert-error">
        <v-icon icon="mdi-alert-circle" size="20" />
        <span>{{ data.error }}</span>
      </div>
    </Transition>

    <!-- Password Form -->
    <form class="password-form" @submit.prevent="updatePw">
      <!-- Current Password -->
      <div class="form-group">
        <label class="form-label">{{ $t('dashboard.account.security.currentPassword') }}</label>
        <input
          v-model="data.oldPassword"
          type="password"
          class="form-input"
          :disabled="data.requestedOtp"
          :class="{ disabled: data.requestedOtp }"
          :placeholder="$t('dashboard.account.security.currentPasswordPlaceholder')"
          autocomplete="current-password"
        />
      </div>

      <!-- New Password -->
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">{{ $t('dashboard.account.security.newPassword') }}</label>
          <input
            v-model="data.newPassword"
            type="password"
            class="form-input"
            :disabled="data.requestedOtp"
            :class="{ disabled: data.requestedOtp }"
            :placeholder="$t('dashboard.account.security.newPasswordPlaceholder')"
            autocomplete="new-password"
          />
          <PwStrengthMeter :password="data.newPassword" @pw-strength="handlePwStrength" />
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('dashboard.account.security.confirmPassword') }}</label>
          <input
            v-model="data.newPasswordRetype"
            type="password"
            class="form-input"
            :disabled="data.requestedOtp"
            :class="{
              disabled: data.requestedOtp,
              error: !passwordsMatch
            }"
            :placeholder="$t('dashboard.account.security.confirmPasswordPlaceholder')"
            autocomplete="new-password"
          />
          <span v-if="!passwordsMatch" class="form-error">
            {{ $t('dashboard.account.security.passwordsNoMatch') }}
          </span>
        </div>
      </div>

      <!-- OTP Input (when requested) -->
      <Transition name="slide-fade">
        <div v-if="data.requestedOtp" class="form-group otp-group">
          <label class="form-label">{{ $t('dashboard.account.security.confirmationCode') }}</label>
          <input
            v-model="data.otp"
            type="text"
            class="form-input otp-input"
            :placeholder="$t('dashboard.account.security.confirmationCodePlaceholder')"
            maxlength="6"
            autocomplete="one-time-code"
          />
          <span class="form-hint">
            {{ $t('dashboard.account.security.confirmationCodeHint') }}
          </span>
        </div>
      </Transition>

      <!-- Form Actions -->
      <div class="form-actions">
        <button
          type="button"
          class="btn btn-secondary"
          @click="revokeChanges"
          :disabled="data.loading"
        >
          {{ $t('dashboard.account.security.cancel') }}
        </button>
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="!isFormValid || data.loading"
        >
          <v-progress-circular v-if="data.loading" indeterminate size="18" width="2" />
          <v-icon v-else :icon="data.requestedOtp ? 'mdi-check' : 'mdi-send'" size="18" />
          <span>{{ data.loading ? $t('dashboard.account.security.processing') : (data.requestedOtp ? $t('dashboard.account.security.changePassword') : $t('dashboard.account.security.requestOtp')) }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.password-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Card Header */
.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.2);
  border-radius: 12px;
  color: #ffb74d;
  flex-shrink: 0;
}

.card-info {
  flex: 1;
}

.card-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 4px;
}

.card-description {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

/* Alerts */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
}

.alert-success {
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  color: #66bb6a;
}

.alert-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  color: #ef5350;
}

/* Form */
.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 600px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.otp-group {
    max-width: 200px;
  }
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.95);
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: #00B0FF;
    background: rgba(0, 176, 255, 0.05);
    box-shadow: 0 0 0 3px rgba(0, 176, 255, 0.1);
  }

  &:disabled,
  &.disabled {
    background: rgba(255, 255, 255, 0.02);
    border-color: rgba(120, 163, 255, 0.08);
    color: rgba(255, 255, 255, 0.6);
    cursor: not-allowed;
  }

  &.error {
    border-color: rgba(244, 67, 54, 0.5);

    &:focus {
      box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);
    }
  }
}

.otp-input {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 1.25rem;
  letter-spacing: 0.3em;
  text-align: center;
}

.form-hint {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
}

.form-error {
  font-size: 0.8rem;
  color: #ef5350;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-primary {
  background: rgba(0, 176, 255, 0.15);
  color: #00B0FF;
  border: 1px solid rgba(0, 176, 255, 0.3);

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.25);
    box-shadow: 0 0 20px rgba(0, 176, 255, 0.15);
  }
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(200, 218, 255, 0.7);
  border: 1px solid rgba(120, 163, 255, 0.15);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Light Theme */
.v-theme--lightTheme {
  .card-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .card-description {
    color: rgba(15, 23, 42, 0.5);
  }

  .card-icon {
    background: rgba(255, 152, 0, 0.08);
    border-color: rgba(255, 152, 0, 0.15);
  }

  .form-label {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-input {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.95);

    &::placeholder {
      color: rgba(15, 23, 42, 0.3);
    }

    &:focus {
      border-color: #1c6ee3;
      background: rgba(28, 110, 227, 0.03);
      box-shadow: 0 0 0 3px rgba(28, 110, 227, 0.1);
    }

    &:disabled,
    &.disabled {
      background: rgba(0, 0, 0, 0.02);
      color: rgba(15, 23, 42, 0.5);
    }
  }

  .form-hint {
    color: rgba(15, 23, 42, 0.4);
  }

  .btn-primary {
    background: rgba(28, 110, 227, 0.1);
    color: #1c6ee3;
    border-color: rgba(28, 110, 227, 0.2);
  }

  .btn-secondary {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(15, 23, 42, 0.7);
    border-color: rgba(28, 110, 227, 0.15);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;

    .btn {
      width: 100%;
      justify-content: center;
    }
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .form-input,
  .btn,
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }
}
</style>
